import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../api";
import { clearAuthStorage } from "../authStorage";
import { ThemeContext } from "../ThemeContext";
import { UserContext } from "../UserContext";
import { completeGoogleSignIn } from "../utils/cognitoOAuth";

const buildUserFromProfile = (profile, fallback = {}) => ({
  user_id: profile.user_id || fallback.userId || "",
  email: profile.email || fallback.email || "",
  firstName: profile.first_name || fallback.firstName || "",
  lastName: profile.last_name || fallback.lastName || "",
  screenName: profile.screen_name || fallback.screenName || "",
  is_premium: Boolean(profile.is_premium),
  trial_used: Boolean(profile.trial_used),
  created_date: profile.created_date || new Date().toISOString(),
});

const AuthCallbackPage = () => {
  const { setUser } = useContext(UserContext);
  const { isDarkMode } = useContext(ThemeContext);
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let isMounted = true;

    const finishGoogleSignIn = async () => {
      try {
        const { allowCreate, idPayload, tokens } = await completeGoogleSignIn();
        const response = await api.post(
          "/api/user/sync_oauth_user/",
          { allow_create: allowCreate },
          {
            headers: {
              Authorization: `Bearer ${tokens.id_token}`,
            },
          },
        );
        const user = buildUserFromProfile(response.data, {
          email: idPayload.email,
          firstName: idPayload.given_name,
          lastName: idPayload.family_name,
          screenName: idPayload.name,
          userId: idPayload.sub?.replace(/-/g, ""),
        });

        localStorage.setItem("sat_user", JSON.stringify(user));

        const referralCode = localStorage.getItem("pending_referral_code");
        if (referralCode && user.email) {
          try {
            await api.post("/api/refer/mark_referred/", {
              referral_code: referralCode,
              email: user.email,
            });
            localStorage.removeItem("pending_referral_code");
          } catch (error) {
            console.error("Error marking referral:", error);
          }
        }

        if (!isMounted) return;

        setUser(user);
        const redirectUrl = localStorage.getItem("redirect_url") || "/options";
        localStorage.removeItem("redirect_url");
        navigate(redirectUrl, { replace: true });
      } catch (error) {
        clearAuthStorage();

        if (!isMounted) return;

        setErrorMessage(
          error.response?.data?.error ||
            error.message ||
            "Google sign-in could not be completed.",
        );
      }
    };

    finishGoogleSignIn();

    return () => {
      isMounted = false;
    };
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center font-zonaProRegular p-4 pb-24 pl-0 md:pl-24 bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <motion.div
        className="w-full max-w-xl backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-3xl shadow-2xl p-6 sm:p-8 border border-white/20 dark:border-gray-700/50 text-center"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <h1
          className="text-2xl sm:text-3xl font-bold font-zonaProUltra mb-4"
          style={{ color: isDarkMode ? "#ff7a00" : "#0a1a2f" }}
        >
          GOOGLE SIGN-IN
        </h1>

        {errorMessage ? (
          <>
            <p className="text-red-600 dark:text-red-300 bg-red-100 dark:bg-red-900/30 rounded-xl p-4 text-sm sm:text-base mb-6">
              {errorMessage}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/signup"
                className="py-3 px-6 bg-[#34c759] text-white text-base font-zonaProUltra rounded-full hover:shadow-lg hover:shadow-[#34c759]/50 transition-all"
              >
                SIGN UP
              </Link>
              <Link
                to="/login"
                className="py-3 px-6 border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-100 text-base font-zonaProUltra rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-all"
              >
                LOGIN
              </Link>
            </div>
          </>
        ) : (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto mb-4" />
            <p className="text-gray-700 dark:text-gray-300 text-sm sm:text-base">
              Completing Google sign-in...
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default AuthCallbackPage;
